// Noise filters for Sentry, applied in code because inbound filters are not
// on our Sentry plan. Always on; wired in src/index.js.

export const IGNORE_ERRORS = [
  /Load failed/,
  /getModifierState is not a function/,
  /Non-Error promise rejection captured/,
];

// Network noise (axios XHR faults and the fetch API "Failed to fetch"). Dropped
// in beforeSend, not ignoreErrors, so the auth-flow failures that logAuthError
// tags flow:auth are kept: an outage must show up. The two auth steps that
// carry no information are dropped by isAuthNetworkFault.
const NETWORK_NOISE = [/Network Error/, /Request aborted/, /Failed to fetch/];

const eventMessages = (event, hint) =>
  [
    hint?.originalException?.message,
    hint?.originalException,
    event?.message,
    ...(event?.exception?.values || []).map((v) =>
      [v?.type, v?.value].filter(Boolean).join(": ")
    ),
  ].filter((m) => typeof m === "string");

export const isNetworkNoise = (event, hint) =>
  event?.tags?.flow !== "auth" &&
  eventMessages(event, hint).some((m) => NETWORK_NOISE.some((p) => p.test(m)));

// A network fault of an auth step that never got a response (status 0): the
// browser lost the connection, or the request was aborted. The event holds no
// information and no one can act on it. Only these steps and messages are
// dropped (logAuthError tags the step). A password-login fault is kept, and so
// is any error with a response: an outage of cy-sso shows up as a 5xx.
const AUTH_NETWORK_FAULTS = {
  "email-check": [/Network Error/, /Failed to fetch/],
  "token-refresh": [/Network Error/, /Request aborted/],
};

export const isAuthNetworkFault = (event, hint) => {
  const patterns = AUTH_NETWORK_FAULTS[event?.tags?.authStep];
  return (
    event?.tags?.flow === "auth" &&
    Array.isArray(patterns) &&
    !hint?.originalException?.response &&
    eventMessages(event, hint).some((m) => patterns.some((p) => p.test(m)))
  );
};

// A 401 that the logout flow already handled (createApiClient and
// mfeRefreshCoordinator tag the error sessionExpiredHandled). The student is
// sent to the login page, so the rejected request is not a fault.
export const isHandledSessionExpiry = (hint) => {
  const err = hint?.originalException;
  return err?.sessionExpiredHandled === true && err?.response?.status === 401;
};

// An unhandled rejection of the refresh call: a 401 (the session is dead and
// createApiClient already ended it), a lost refresh race, or a page with no
// local session. The callers of the first request do not catch the rejection.
const isRefresh401 = (event, hint) => {
  const err = hint?.originalException;
  return (
    event?.tags?.flow !== "auth" &&
    (err?.refreshRaceLost === true ||
      err?.noLocalSession === true ||
      (err?.response?.status === 401 &&
        /api\/login\/validate\/refresh/.test(err?.config?.url || "")))
  );
};

// A student with a pre cy-sso token is logged out on purpose, and the pending
// request is rejected with this message. It is a planned end of session, so it
// is dropped whatever the tag. The Sandbox app sends it through this client too.
const EXPECTED_LOGOUT = /LEGACY_SESSION_LOGOUT/;

export const isExpectedLogout = (event, hint) =>
  eventMessages(event, hint).some((m) => EXPECTED_LOGOUT.test(m));

// Scripts injected by browser extensions and by in-app browsers (iabjs://, for
// example the one of a social app on Android).
export const DENY_URLS = [
  /^iabjs:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
];

// Events sent by our own browser extensions carry these release names. App
// releases are the commit SHA (REACT_APP_SENTRY_RELEASE), which never matches.
const EXTENSION_RELEASES = [
  /^browser-extension@/,
  /^koala-inspector@/,
  /^app-extension@/,
  /^rbc-chrome-extension/,
  /-chrome$/,
  /-edge$/,
];

export const isExtensionRelease = (release) =>
  typeof release === "string" &&
  EXTENSION_RELEASES.some((pattern) => pattern.test(release));

// Returning null drops the event.
export const beforeSend = (event, hint) =>
  isExtensionRelease(event?.release) ||
  isNetworkNoise(event, hint) ||
  isAuthNetworkFault(event, hint) ||
  isHandledSessionExpiry(hint) ||
  isRefresh401(event, hint) ||
  isExpectedLogout(event, hint)
    ? null
    : event;

export const filterOptions = () => ({
  ignoreErrors: IGNORE_ERRORS,
  denyUrls: DENY_URLS,
  beforeSend,
});
